import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload,
  FileUp,
  MapPin,
  Route,
  Hexagon,
  Trash2,
  Download,
  FolderTree,
  Loader2,
  Map as MapIcon,
  List as ListIcon,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import ProjectMapView from './ProjectMapView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_ICONS = {
  Point: MapPin,
  MultiPoint: MapPin,
  LineString: Route,
  MultiLineString: Route,
  Polygon: Hexagon,
  MultiPolygon: Hexagon,
};

const TYPE_LABELS = {
  Point: 'Nöqtə',
  MultiPoint: 'Çoxlu nöqtə',
  LineString: 'Xətt',
  MultiLineString: 'Çoxlu xətt',
  Polygon: 'Sahə',
  MultiPolygon: 'Çoxlu sahə',
};

const TYPE_COLORS = {
  Point: 'text-blue-600 bg-blue-50',
  MultiPoint: 'text-blue-600 bg-blue-50',
  LineString: 'text-emerald-600 bg-emerald-50',
  MultiLineString: 'text-emerald-600 bg-emerald-50',
  Polygon: 'text-amber-600 bg-amber-50',
  MultiPolygon: 'text-amber-600 bg-amber-50',
};

export default function ProjectMapTab({ project, canEdit, onChanged }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [summary, setSummary] = useState({ total: 0, by_type: {}, by_file: {} });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState(null);
  const [filterFile, setFilterFile] = useState(null);
  const [viewMode, setViewMode] = useState('map'); // 'map' | 'list'
  const fileRef = useRef(null);

  const fetchAll = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/projects/${project.id}/geo/summary`, { headers }),
        axios.get(`${API}/projects/${project.id}/geo`, { headers }),
      ]);
      setSummary(s.data || { total: 0, by_type: {}, by_file: {} });
      setItems(l.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Geo məlumatlar yüklənmədi');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(kml|kmz)$/i.test(file.name)) {
      toast.error('Yalnız .kml və .kmz formatları dəstəklənir');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await axios.post(
        `${API}/projects/${project.id}/geo/upload`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      const { inserted, skipped, by_type } = r.data;
      const typeSummary = Object.entries(by_type || {})
        .map(([k, v]) => `${TYPE_LABELS[k] || k}: ${v}`)
        .join(', ');
      toast.success(
        `${inserted} element yükləndi${typeSummary ? ` (${typeSummary})` : ''}${
          skipped > 0 ? ` • ${skipped} atlandı` : ''
        }`
      );
      fetchAll();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Yükləmə uğursuz oldu');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`"${item.name}" silinsin?`)) return;
    try {
      await axios.delete(`${API}/projects/${project.id}/geo/${item.id}`, { headers });
      toast.success('Silindi');
      fetchAll();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinmədi');
    }
  };

  const handleDeleteByFile = async (filename) => {
    if (!window.confirm(`"${filename}" faylından idxal edilmiş bütün elementlər silinsin?`)) return;
    try {
      const r = await axios.delete(
        `${API}/projects/${project.id}/geo?source_file=${encodeURIComponent(filename)}`,
        { headers }
      );
      toast.success(`${r.data.deleted} element silindi`);
      fetchAll();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinmədi');
    }
  };

  const handleDownloadGeoJSON = async () => {
    try {
      const r = await axios.get(
        `${API}/projects/${project.id}/geo?as_geojson=true`,
        { headers }
      );
      const blob = new Blob([JSON.stringify(r.data, null, 2)], {
        type: 'application/geo+json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name || 'project'}-geo.geojson`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Yüklənmədi');
    }
  };

  const filtered = items.filter((it) => {
    if (filterType && it.geometry_type !== filterType) return false;
    if (filterFile && it.source_file !== filterFile) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !(it.name || '').toLowerCase().includes(q) &&
        !(it.description || '').toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  return (
    <div data-testid="project-map-tab" className="space-y-4">
      {/* Upload + Actions toolbar */}
      <div className="border rounded-lg p-4 bg-gradient-to-br from-purple-50 to-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-zinc-800 flex items-center gap-2">
              <FileUp className="h-4 w-4 text-purple-600" />
              KMZ / KML idxalı
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              Google Earth və ya GIS ixrac faylları yükləyin (max 20 MB).
              Hər Placemark avtomatik olaraq GeoJSON kimi saxlanılır.
            </div>
          </div>
          <div className="flex gap-2">
            {summary.total > 0 && (
              <div className="inline-flex rounded-md border bg-white p-0.5" role="tablist">
                <button
                  onClick={() => setViewMode('map')}
                  className={`px-2.5 py-1 text-xs rounded flex items-center gap-1 transition ${
                    viewMode === 'map' ? 'bg-purple-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'
                  }`}
                  data-testid="view-map-btn"
                >
                  <MapIcon className="h-3.5 w-3.5" /> Xəritə
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-2.5 py-1 text-xs rounded flex items-center gap-1 transition ${
                    viewMode === 'list' ? 'bg-purple-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'
                  }`}
                  data-testid="view-list-btn"
                >
                  <ListIcon className="h-3.5 w-3.5" /> Siyahı
                </button>
              </div>
            )}
            {summary.total > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownloadGeoJSON}
                data-testid="download-geojson-btn"
              >
                <Download className="h-3.5 w-3.5 mr-1" />
                GeoJSON yüklə
              </Button>
            )}
            {canEdit && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
                  className="hidden"
                  onChange={handleUpload}
                  data-testid="geo-file-input"
                />
                <Button
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="upload-geo-btn"
                >
                  {uploading ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <Upload className="h-3.5 w-3.5 mr-1" />
                  )}
                  Fayl seç
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      {summary.total > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard
            label="Cəmi element"
            value={summary.total}
            color="bg-purple-50 text-purple-700"
            onClick={() => {
              setFilterType(null);
              setFilterFile(null);
            }}
            active={!filterType && !filterFile}
            testid="summary-total"
          />
          {Object.entries(summary.by_type).map(([k, v]) => {
            const Icon = TYPE_ICONS[k] || MapPin;
            return (
              <SummaryCard
                key={k}
                label={TYPE_LABELS[k] || k}
                value={v}
                icon={Icon}
                color={TYPE_COLORS[k] || 'bg-zinc-50 text-zinc-700'}
                onClick={() => setFilterType(filterType === k ? null : k)}
                active={filterType === k}
                testid={`summary-type-${k}`}
              />
            );
          })}
        </div>
      )}

      {/* Source files */}
      {Object.keys(summary.by_file || {}).length > 0 && (
        <div className="border rounded-lg p-3 bg-white">
          <div className="text-xs font-semibold text-zinc-600 mb-2 flex items-center gap-1.5">
            <FolderTree className="h-3.5 w-3.5" />
            İdxal mənbələri
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(summary.by_file).map(([file, count]) => (
              <div
                key={file}
                className={`flex items-center gap-2 border rounded-md px-2 py-1 text-xs ${
                  filterFile === file ? 'bg-purple-50 border-purple-300' : 'bg-zinc-50'
                }`}
              >
                <button
                  onClick={() => setFilterFile(filterFile === file ? null : file)}
                  className="flex items-center gap-1.5"
                  data-testid={`filter-file-${file}`}
                >
                  <span className="font-medium truncate max-w-[200px]">{file}</span>
                  <Badge variant="secondary" className="text-[10px]">
                    {count}
                  </Badge>
                </button>
                {canEdit && (
                  <button
                    onClick={() => handleDeleteByFile(file)}
                    className="text-zinc-400 hover:text-red-600"
                    title="Bu fayldan idxal olanları sil"
                    data-testid={`delete-file-${file}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conditional view: Map (default) or List */}
      {viewMode === 'map' || summary.total === 0 ? (
        <ProjectMapView project={project} canEdit={canEdit} onChanged={fetchAll} />
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b bg-zinc-50 flex items-center justify-between gap-3">
            <div className="text-xs text-zinc-600">
              {filtered.length} / {items.length} element
              {(filterType || filterFile) && (
                <button
                  onClick={() => {
                    setFilterType(null);
                    setFilterFile(null);
                  }}
                  className="ml-2 text-purple-600 hover:underline"
                  data-testid="clear-filters-btn"
                >
                  filtrləri sıfırla
                </button>
              )}
            </div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ad və ya açıqlama üzrə axtarış..."
              className="max-w-xs h-8 text-sm"
              data-testid="geo-search-input"
            />
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading && (
              <div className="p-4 text-sm text-zinc-500">Yüklənir...</div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="p-4 text-sm text-zinc-500">Heç nə tapılmadı</div>
            )}
            {!loading &&
              filtered.map((it) => {
                const Icon = TYPE_ICONS[it.geometry_type] || MapPin;
                const colorCls = TYPE_COLORS[it.geometry_type] || 'text-zinc-600 bg-zinc-100';
                return (
                  <div
                    key={it.id}
                    className="flex items-start gap-3 px-3 py-2 border-b hover:bg-zinc-50"
                    data-testid={`geo-item-${it.id}`}
                  >
                    <div className={`p-1.5 rounded-md ${colorCls}`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{it.name || '(adsız)'}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-[10px]">
                          {TYPE_LABELS[it.geometry_type] || it.geometry_type}
                        </Badge>
                        {it.properties?.folder_path && (
                          <span className="text-[10px] text-zinc-500 truncate">
                            <FolderTree className="h-2.5 w-2.5 inline mr-0.5" />
                            {it.properties.folder_path}
                          </span>
                        )}
                        {it.source_file && (
                          <span className="text-[10px] text-zinc-400 truncate">
                            {it.source_file}
                          </span>
                        )}
                      </div>
                      {it.description && (
                        <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                          {it.description}
                        </div>
                      )}
                    </div>
                    {canEdit && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-zinc-400 hover:text-red-600"
                        onClick={() => handleDeleteItem(it)}
                        data-testid={`delete-geo-${it.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color, onClick, active, testid }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg p-3 text-left border-2 transition-all ${
        active
          ? 'border-purple-400 bg-purple-50'
          : 'border-transparent hover:border-zinc-200 bg-white'
      }`}
      data-testid={testid}
    >
      <div className={`inline-flex items-center justify-center h-8 w-8 rounded-md ${color}`}>
        {Icon ? <Icon className="h-4 w-4" /> : <span className="text-xs font-bold">Σ</span>}
      </div>
      <div className="text-2xl font-bold mt-1.5 text-zinc-800">{value}</div>
      <div className="text-xs text-zinc-500">{label}</div>
    </button>
  );
}
